import { isNaN } from "lodash";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * Formats a number according to the specified locale's conventions
 * @param {number} value - The number to format
 * @param {string} [locale='en-US'] - The locale to use for formatting (e.g., 'en-US', 'en-GB', 'de-DE')
 * @param {object} [options] - Intl.NumberFormat options
 * @param {number} [options.minimumFractionDigits] - Minimum number of fraction digits
 * @param {number} [options.maximumFractionDigits] - Maximum number of fraction digits
 * @param {boolean} [options.useGrouping=true] - Whether to use grouping separators
 * @returns {string} The formatted number as a string
 * @example
 * formatNumber(1234.567) // Returns "1,234.57"
 * formatNumber(1234.567, 'de-DE') // Returns "1.234,57"
 * formatNumber(1234.567, 'en-US', { maximumFractionDigits: 1 }) // Returns "1,234.6"
 */
export const formatNumber = (value, locale = "en-US", options = {}) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "";
  }

  const defaultOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true,
    ...options,
  };

  return new Intl.NumberFormat(locale, defaultOptions).format(value);
};

export const formatReportRangeDate = (localDate, isFromDate = false) => {
  if (!localDate) return "";
  if (isFromDate) {
    return dayjs(localDate).endOf("day").utc().toISOString();
  }
  return dayjs(localDate).startOf("day").utc().toISOString();
};

export const formatUTCToLocal = (utcDate, format = "MM/DD/YYYY") => {
  if (!utcDate) return "";
  const currentTime = dayjs().format("HH:mm:ss");
  const newUtcDate = dayjs.utc(utcDate);

  const localDate = newUtcDate.local();
  // Add current time to localDate
  const [hours, minutes, seconds] = currentTime.split(":");
  localDate.set("hour", parseInt(hours, 10));
  localDate.set("minute", parseInt(minutes, 10));
  localDate.set("second", parseInt(seconds, 10));
  return localDate.format(format);
};

/**
 * Formats a status string from snake_case to Title Case
 * @param {string} status - The status string in snake_case (e.g., "messages", "mail_managed")
 * @returns {string} The formatted status in Title Case (e.g., "Messages", "Mail Managed")
 * @example
 * formatStatusToTitle("messages") // Returns "Messages"
 * formatStatusToTitle("mail_managed") // Returns "Mail Managed"
 * formatStatusToTitle("after_hours_engagements") // Returns "After Hours Engagements"
 */
export const formatStatusToTitle = status => {
  if (!status) return "";
  return status
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// BE's generic SMS/MMS message type — displayed as "SMS" rather than the
// literal "Normal" title-casing would otherwise produce.
const MESSAGE_TYPE_NORMAL = "NORMAL";

/**
 * Formats a message type string into readable plain English
 * @param {string} messageType - The message type (e.g., "Webchat_Widget", "WIDGET_CHAT", "SMS")
 * @returns {string} The formatted message type (e.g., "Webchat Widget", "Widget Chat", "Sms")
 */
export const formatMessageType = messageType => {
  if (!messageType) return "";
  if (messageType.toLowerCase() === MESSAGE_TYPE_NORMAL.toLowerCase()) return "SMS";
  return messageType
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Format phone number to US format
 * @param {string|number} phoneNumber - The phone number to format
 * @returns {string} The formatted phone number
 * @example
 * formatUSPhoneNumber("1234567890") // Returns "(123) 456-7890"
 * formatUSPhoneNumber("+11234567890") // Returns "+1 (123) 456-7890"
 * formatUSPhoneNumber("123-456-7890") // Returns "(123) 456-7890"
 * formatUSPhoneNumber("") // Returns "--"
 */
export const formatUSPhoneNumber = phoneNumber => {
  if (!phoneNumber) return "--";

  // Remove all non-numeric characters except +
  const cleaned = phoneNumber.toString().replace(/[^\d+]/g, "");

  // Check if it has country code
  const hasCountryCode = cleaned.startsWith("+1") || cleaned.startsWith("1");
  const digits = cleaned.replace(/^\+?1/, ""); // Remove country code

  // Check if we have enough digits
  if (digits.length === 10) {
    const areaCode = digits.substring(0, 3);
    const middle = digits.substring(3, 6);
    const last = digits.substring(6, 10);

    if (hasCountryCode) {
      return `+1 (${areaCode}) ${middle}-${last}`;
    }
    return `(${areaCode}) ${middle}-${last}`;
  }

  // If not 10 digits, return as-is
  return phoneNumber;
};
